"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { Edit2, Inbox, Loader2, PieChart, Search } from "lucide-react";
import { useState } from "react";
import { usePeriodeStore } from "@/hooks/use-periode";
import { useMasterPeriode } from "@/hooks/use-master-periode";
import { apiClient } from "@/lib/api-client";

// --- Interfaces ---
export interface PeriodeUnsurMaxData {
  id: number;
  master_periode_id: number;
  master_unsur_id: number;
  kode_unsur: string;
  nama_unsur: string;
  persentase: number;
  point_tp: number;
  point_aa: number;
  point_l: number;
  point_lk: number;
  point_gb: number;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

interface PeriodeUnsurMaxResponse {
  datas: PeriodeUnsurMaxData[];
}

// --- API FUNCTIONS ---
const fetchPeriodeUnsurMax = async (
  id: number,
): Promise<PeriodeUnsurMaxData[]> => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;

  const result = await apiClient<PeriodeUnsurMaxResponse>(
    `${baseUrl}/api/v1/master-periode-unsur-max/${id}`,
    {
      headers: {
        Accept: "application/json",
      },
    },
  );

  return result.datas;
};

export default function PeriodeNilaiMaxUnsurPage() {
  const [searchQuery, setSearchQuery] = useState("");

  // --- PERIOD SYNC ---
  const { activePeriod } = usePeriodeStore();
  const { data: masterPeriodes } = useMasterPeriode();

  const currentPeriodObj = masterPeriodes?.find(
    (p) => `${p.tahun}-${p.semester}` === activePeriod,
  );
  const master_periode_id = currentPeriodObj?.master_periode_id;

  // --- TanStack React Query ---
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["periode-unsur-max", master_periode_id],
    queryFn: () => fetchPeriodeUnsurMax(master_periode_id!),
    enabled: !!master_periode_id,
  });

  // --- Filter Logic ---
  const filteredData = data?.filter(
    (item) =>
      item.nama_unsur.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.kode_unsur.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // --- UI Helpers ---
  const getPercentageColor = (persentase: number) => {
    if (persentase >= 30) return "bg-blue-100 text-blue-800 border-blue-200";
    if (persentase >= 15)
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    return "bg-slate-100 text-slate-800 border-slate-200";
  };

  return (
    <div>
      <Card className="shadow-sm border-slate-200 overflow-hidden">
        <CardHeader className="bg-slate-50/80 border-b border-slate-100 pb-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-1.5 flex flex-row items-center gap-3">
              <div className="p-2.5 bg-indigo-100/50 rounded-lg border border-indigo-200/50 hidden sm:block">
                <PieChart className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">
                  Batas Maksimal Unsur
                </CardTitle>
                <CardDescription className="text-slate-500 text-sm mt-1">
                  Atur persentase bobot dan poin maksimal per unsur kegiatan
                  untuk tiap jabatan fungsional.
                </CardDescription>
              </div>
            </div>

            {/* Toolbar Area */}
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Cari kode atau unsur..."
                  className="pl-9 bg-white border-slate-200 focus-visible:ring-indigo-500 shadow-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Badge
                variant="secondary"
                className="w-full sm:w-auto justify-center bg-indigo-50/80 text-indigo-700 hover:bg-indigo-50/80 border-indigo-200 shadow-sm px-4 py-2 text-sm font-semibold whitespace-nowrap"
              >
                Periode Aktif:{" "}
                {currentPeriodObj
                  ? `${currentPeriodObj.semester === 1 ? "Ganjil" : "Genap"} ${currentPeriodObj.tahun}/${parseInt(currentPeriodObj.tahun) + 1}`
                  : "..."}
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-white">
                <TableRow className="border-b-slate-200 hover:bg-transparent">
                  <TableHead className="w-24 pl-6 text-slate-600 font-semibold">
                    Kode
                  </TableHead>
                  <TableHead className="min-w-62.5 text-slate-600 font-semibold">
                    Nama Unsur
                  </TableHead>
                  <TableHead className="text-center w-28 text-slate-600 font-semibold border-x border-slate-100 bg-slate-50/30">
                    Persentase
                  </TableHead>
                  <TableHead className="bg-blue-50/40 text-blue-700 text-center font-bold border-r border-blue-100/50 w-24">
                    TP
                  </TableHead>
                  <TableHead className="bg-blue-50/40 text-blue-700 text-center font-bold border-r border-blue-100/50 w-24">
                    AA
                  </TableHead>
                  <TableHead className="bg-blue-50/40 text-blue-700 text-center font-bold border-r border-blue-100/50 w-24">
                    L
                  </TableHead>
                  <TableHead className="bg-blue-50/40 text-blue-700 text-center font-bold border-r border-blue-100/50 w-24">
                    LK
                  </TableHead>
                  <TableHead className="bg-blue-50/40 text-blue-700 text-center font-bold border-r border-blue-100/50 w-24">
                    GB
                  </TableHead>
                  <TableHead className="text-right pr-6 w-24 text-slate-600 font-semibold">
                    Aksi
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {/* 1. Loading State */}
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={10} className="h-64 text-center">
                      <div className="flex flex-col items-center justify-center text-slate-400 space-y-3">
                        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                        <p className="text-sm">Memuat data batas unsur...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* 2. Error State */}
                {isError && (
                  <TableRow>
                    <TableCell colSpan={10} className="h-64 text-center">
                      <div className="text-red-500 font-medium">
                        Gagal memuat data: {(error as Error).message}
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* 3. Data Ter-render */}
                {!isLoading &&
                !isError &&
                filteredData &&
                filteredData.length > 0
                  ? filteredData.map((item) => (
                      <TableRow
                        key={item.id}
                        className="hover:bg-slate-50 transition-colors group"
                      >
                        <TableCell className="pl-6 align-middle py-4">
                          <Badge
                            variant="outline"
                            className="font-mono text-xs bg-white text-slate-600 border-slate-300 shadow-sm"
                          >
                            {item.kode_unsur}
                          </Badge>
                        </TableCell>

                        <TableCell className="align-middle py-4">
                          <span className="text-sm font-semibold text-slate-900">
                            {item.nama_unsur}
                          </span>
                        </TableCell>

                        <TableCell className="text-center align-middle py-4 border-x border-slate-100 bg-slate-50/30">
                          <Badge
                            className={`${getPercentageColor(item.persentase)} shadow-sm`}
                          >
                            {item.persentase}%
                          </Badge>
                        </TableCell>

                        {/* Poin Maksimal per Jabatan Fungsional */}
                        <TableCell className="text-center tabular-nums font-medium text-blue-700 border-r border-blue-50/50 align-middle py-4">
                          {item.point_tp?.toLocaleString() || "0"}
                        </TableCell>
                        <TableCell className="text-center tabular-nums font-medium text-blue-700 border-r border-blue-50/50 align-middle py-4">
                          {item.point_aa?.toLocaleString() || "0"}
                        </TableCell>
                        <TableCell className="text-center tabular-nums font-medium text-blue-700 border-r border-blue-50/50 align-middle py-4">
                          {item.point_l?.toLocaleString() || "0"}
                        </TableCell>
                        <TableCell className="text-center tabular-nums font-medium text-blue-700 border-r border-blue-50/50 align-middle py-4">
                          {item.point_lk?.toLocaleString() || "0"}
                        </TableCell>
                        <TableCell className="text-center tabular-nums font-medium text-blue-700 border-r border-blue-50/50 align-middle py-4">
                          {item.point_gb?.toLocaleString() || "0"}
                        </TableCell>

                        {/* Aksi */}
                        <TableCell className="text-right pr-6 align-middle py-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-slate-400 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Edit2 className="w-4 h-4 mr-2" /> Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  : // 4. Empty State
                    !isLoading &&
                    !isError && (
                      <TableRow>
                        <TableCell colSpan={10} className="h-64 text-center">
                          <div className="flex flex-col items-center justify-center text-slate-500 space-y-3">
                            <div className="bg-slate-50 p-4 rounded-full border border-slate-100">
                              <Inbox className="w-8 h-8 text-slate-300" />
                            </div>
                            <p className="text-sm text-slate-500">
                              Tidak ada unsur yang cocok dengan <br />
                              <span className="font-semibold text-slate-700">
                                "{searchQuery}"
                              </span>
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
